import { useState } from 'react';
import { useProfiles } from '@/api/hooks';
import { useBuildSubmissions, useStoreApps, type Submission } from '@/api/endpointsStores';
import type { Artifact, Build } from '@/api/schemas';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { GatedButton } from '@/components/GatedButton';
import { Icon } from '@/components/Icon';
import { StatusPill, Tag } from '@/components/badges';
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDateTime } from '@/lib/format';
import { stagger } from '@/lib/motion';
import { useCan } from '@/lib/permissions';
import { SubmitBuildDialog } from './SubmitBuildDialog';
import { releaseText, submissionProblem, submissionStatusLabel, submitBlockedReason, submitFormDefaults } from './submissionsModel';

/**
 * "Google Play" on an Android build (P29 phase 2, SPEC §39.4): Submit, and each submission of the build with its status,
 * release and timeline. Shown only when the build's app has a Google Play app (operators add it in the app's Stores tab).
 */
export function BuildSubmissionsCard({ build, artifacts }: { build: Build; artifacts: Artifact[] | undefined }) {
  const storeApps = (useStoreApps(build.platform === 'android' ? build.app.id : undefined).data ?? []).filter((s) => s.platform === 'android');
  const submissions = useBuildSubmissions(build.id, storeApps.length > 0);
  const profile = useProfiles(storeApps.length ? build.app.id : undefined).data?.find((p) => p.id === build.profile.id);
  const can = useCan('submissions:write', build.projectId);
  const [open, setOpen] = useState(false);
  if (storeApps.length === 0) return null;
  return (
    <Card className="enter min-w-0" style={stagger(2)} data-testid="build-submissions-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Icon name="storefront" size={18} className="text-subtle" />
          Google Play {submissions.data?.length ? <Tag className="tabular">{submissions.data.length}</Tag> : null}
        </CardTitle>
        <CardDescription>This build&apos;s submissions to Google Play</CardDescription>
        {can.hidden ? null : (
          <CardAction>
            <GatedButton variant="outline" data-testid="build-submit-open" can={can} blockedReason={submitBlockedReason(build, artifacts)} onClick={() => setOpen(true)} className="max-sm:h-11">
              <Icon name="upload" size={18} />
              Submit
            </GatedButton>
          </CardAction>
        )}
      </CardHeader>
      <CardContent>
        <SubmissionsBody query={submissions} />
      </CardContent>
      <SubmitBuildDialog build={build} storeApps={storeApps} initial={submitFormDefaults(build, profile, storeApps)} open={open} onOpenChange={setOpen} />
    </Card>
  );
}

function SubmissionsBody({ query }: { query: ReturnType<typeof useBuildSubmissions> }) {
  if (query.isLoading && !query.data) return <Skeleton className="h-14 w-full" aria-busy="true" aria-label="Loading the submissions" />;
  if (query.isError && !query.data) return <ErrorState compact error={query.error} onRetry={() => void query.refetch()} />;
  if (!query.data?.length) {
    return <EmptyState compact testId="build-submissions-empty" icon="storefront" title="Not submitted yet" description="Submit sends this build's AAB to a Google Play track. A profile can also submit every build of an environment by itself." />;
  }
  return <ul className="flex flex-col divide-y rounded-lg border" data-testid="build-submissions-list">{query.data.map((s) => <SubmissionRow key={s.id} s={s} />)}</ul>;
}

function SubmissionRow({ s }: { s: Submission }) {
  const problem = submissionProblem(s);
  return (
    <li className="flex min-w-0 flex-col gap-2 px-3.5 py-3" data-testid={`build-submission-${s.track}`} data-status={s.status}>
      <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
        <span className="min-w-0 truncate font-mono text-[13px] font-medium text-foreground">{s.track}</span>
        <StatusPill status={s.status} label={submissionStatusLabel(s.status)} />
      </div>
      <span className="text-[13px] text-muted-foreground">{releaseText(s)} · {s.storeApp?.identifier ?? 'Google Play'}{s.actorLabel ? ` · ${s.actorLabel}` : ''}</span>
      {problem ? <p className="text-[13px] text-destructive" data-testid="build-submission-problem">{problem}</p> : null}
      <ol className="flex flex-col gap-0.5 text-xs text-subtle" aria-label={`Timeline of the ${s.track} submission`}>
        {s.events.map((e) => <li key={e.id} className="flex justify-between gap-3"><span>{submissionStatusLabel(e.toStatus ?? '')}</span><time dateTime={e.createdAt}>{formatDateTime(e.createdAt)}</time></li>)}
      </ol>
    </li>
  );
}
